<template>
  <div v-rect-loading="loading" class="password-container">
    <div class="password-editor-header">
      <div class="password-editor-title">{{ $t(`${i18nPrefix}.title.changePassword`) }}</div>
    </div>
    <div class="password-info-container">
      <WrappedFrom
        hideRequiredAsterisk
        ref="form"
        class="password-info"
        :model="passwordInfo"
        :formLines="formLines"
      >
      </WrappedFrom>
      <div class="bottom-btn-group">
        <el-button class="save-btn" type="primary" @click="handleSave">{{ $t('common.btn.submit') }}</el-button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import WrappedFrom from '@/components/formEditor/wrappedForm.vue'
import { validatePassword } from '@/utils/validator'

export default {
  components: {
    WrappedFrom
  },
  data () {
    return {
      i18nPrefix: 'personal.functions.changePassword',
      loading: false,
      passwordInfo: {
        currentPassword: null,
        password: null,
        confirmPassword: null
      }
    }
  },
  computed: {
    ...mapGetters('account', ['getCurrentAccount']),
    formLines () {
      const lines = {
        currentPassword: {
          label: this.$t(`${this.i18nPrefix}.label.currentPassword`),
          props: {
            type: 'password',
            showPassword: true
          },
          rules: [
            { required: true, message: this.$t('common.message.requiredFormField'), trigger: 'blur' }
          ]
        },
        password: {
          label: this.$t(`${this.i18nPrefix}.label.newPassword`),
          props: {
            type: 'password',
            showPassword: true
          },
          rules: [
            { required: true, message: this.$t('common.message.requiredFormField'), trigger: 'blur' },
            { validator: validatePassword, trigger: 'blur' },
            { validator: this.validateSamePassword, trigger: 'blur' }
          ]
        },
        confirmPassword: {
          label: this.$t(`${this.i18nPrefix}.label.confirmPassword`),
          props: {
            type: 'password',
            showPassword: true
          },
          rules: [
            { required: true, message: this.$t('common.message.requiredFormField'), trigger: 'blur' },
            { validator: validatePassword, trigger: 'blur' },
            { validator: this.validateSamePassword, trigger: 'blur' }
          ]
        }
      }
      return lines
    },
    form () {
      return this.$refs.form
    }
  },
  created () {
  },
  methods: {
    ...mapActions('personal', ['updatePassword']),
    async handleSave () {
      const valid = await this.form.submit()
      if (!valid) {
        return
      }
      this.loading = true
      try {
        const passwordInfo = this.passwordInfo
        const resp = await this.updatePassword({
          old_password: passwordInfo.currentPassword,
          new_password: passwordInfo.password,
          confirm_password: passwordInfo.confirmPassword
        })
        if (resp) {
          this.$message.success(this.$t(`${this.i18nPrefix}.message.updateSuccess`))
          this.passwordInfo = {
            currentPassword: null,
            password: null,
            confirmPassword: null
          }
          this.$eventBus.$emit('changeHomePageInfo')
        }
      } finally {
        this.loading = false
      }
    },
    async validateSamePassword (rule, value, callback) {
      if (value && value === this.passwordInfo.currentPassword) {
        callback(new Error(this.$t(`${this.i18nPrefix}.message.passwordSameAsPrevious`)))
      }
      if (!this.passwordInfo.password || !this.passwordInfo.confirmPassword) {
        callback()
        return
      }
      if (this.passwordInfo.password !== this.passwordInfo.confirmPassword) {
        callback(new Error(this.$t(`${this.i18nPrefix}.message.passwordNotSame`)))
      } else {
        callback()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.password-container {
  min-height: calc(100vh - var(--header-height, 124px) - 28px);
  min-height: calc(100dvh - var(--header-height, 124px) - 28px);
  background-color: #fff;

  .password-editor-header {
    display: flex;
    position: sticky;
    top: -14px;
    height: 52px;
    padding: 16px 0 16px 40px;
    z-index: 3;
    background-color: #fff;
    box-shadow: 0px 2px 4px 0px rgba(0, 0, 0, 0.08);

    .password-editor-title {
      font-size: 16px;
      font-weight: 500;
      line-height: 22px;
      color: #606266;
    }
  }

  .password-info-container {
    width: 570px;
    margin: 80px auto 0;

    ::v-deep {
      .el-form-item {
        margin-bottom: 30px;
      }
    }

    .bottom-btn-group {
      display: flex;
      justify-content: flex-end;
      margin: 30px auto 0;

      .el-button {
        padding: 8px 28px;
        border-radius: 2px;
        line-height: 22px;
        font-size: 16px;
        font-weight: 500;
        color: #0096cc;

        &:last-child {
          margin-left: 24px;
          margin-right: 0;
          background: #0096cc;
          border-color: #0096cc;
          color: #ffffff;
        }
      }
    }
  }
}
</style>
